"use client";

import { FC, useState, useEffect } from "react";
import dynamic from "next/dynamic";
import Conditional from "@repo/shared-ui/Conditional";
import { BottomNavigationProps } from "../../interfaces/IHeader.interface";
import {
  getChildByNodeName,
  openRedirectURL,
} from "@repo/shared-utils/common-utils";
import BFLLink from "@repo/shared-ui/BFLLink";
import { isOpenInApp } from "@repo/shared-utils/pwa-utils";
import { HAMBURGER_PROMPT_URL } from "../../constants/HeaderConstants";
import {
  handleBottomNavigationIconClickDL,
} from "./MobileNavigationAnalyticsFunction";

const HamburgerMenu: any = dynamic(() => import("./HamburgerMenu.tsx") as any, {
  ssr: false,
});
const QuickActions: any = dynamic(() => import("./QuickActions.tsx") as any, {
  ssr: false,
});

const getNavigationLabel = (item: any, index: number) => {
  const label = item?.label || item?.navigationText || item?.iconAltText || "";
  if (index === 1 && ["Account", "My Account"].includes(label)) {
    return "Service";
  }
  return label;
};

const getNavigationHref = (item: any) =>
  item?.ctaLink || item?.navigationUrlLoggedIn || item?.navigationUrlNotLoggedIn;

const getNavigationIconClass = (item: any) =>
  item?.iconName ? `bf-icon-${item.iconName} text-[24px]` : "";

const getNavigationImage = (item: any, isQuickActions: boolean) =>
  item?.scanQrGif || (isQuickActions ? item?.primaryImage : "");

const getNormalizedNavigationItems = (children?: any[]) => {
  if (!children?.length) return [];

  const items = children.map((item, index) => ({
    ...item,
    label: getNavigationLabel(item, index),
    ctaLink: getNavigationHref(item),
    iconClass: getNavigationIconClass(item),
  }));

  return items;
};

const BottomNavigation: FC<BottomNavigationProps> = ({
  bottomnavigationmobile,
  bottomNavigationMenu,
  isAppLayout = false,
}) => {
  // State to manage which panel is active: 'none', 'hamburger', or 'quickActions'
  const [activePanel, setActivePanel] = useState<"none" | "hamburger" | "quickActions">("none");

  // Helper function to get CSS classes for navigation items
  const getNavigationItemClasses = (isQuickActions: boolean, isHamburger: boolean): string => {
    if (isQuickActions && activePanel === "quickActions") {
      return "bg-lgray-200 rounded-[0_0_45%_45%] text-orange-500";
    }
    if (isHamburger && activePanel === "hamburger") {
      return "text-orange-500 content-[''] before:absolute before:left-0 before:top-0 before:h-[3px] before:w-full before:bg-orange-500 before:content-['']";
    }
    return "text-bfl-blue";
  };

  useEffect(() => {
    if (activePanel !== "none") {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [activePanel]);

  // Helper function to safely extract menu items
  const extractMenuItems = (node: any): any[] => {
    if (!node) return [];
    if (Array.isArray(node)) return node;
    if (node.children && Array.isArray(node.children)) return node.children;
    return [];
  };

  // Find the hamburger menu from bottomnavigationmobile structure
  const hamburgerMenuItem = bottomnavigationmobile?.[0]?.children?.find(
    (item: any) => item.nodeName === "hamburgermenu"
  );

  // Get the menu lists using getChildByNodeName
  const hamburgerMenuListNode = getChildByNodeName(hamburgerMenuItem, "hamburgerMenuList");
  const mainMenuListNode = getChildByNodeName(hamburgerMenuItem, "mainMenuList");
  const chipsDetailsNode = getChildByNodeName(hamburgerMenuItem, "chipsDetails");

  const mobileFollowus = getChildByNodeName(hamburgerMenuItem, "followUsMenuList");
  // Extract the actual menu items
  const chipsDetails = extractMenuItems(chipsDetailsNode);
  const hamburgerMenuList = extractMenuItems(hamburgerMenuListNode);
  const mainMenuList = extractMenuItems(mainMenuListNode);

  const quickActions =
    bottomNavigationMenu?.children?.find((item) => item.nodeName === "item2") || {};
  const navigationItems = getNormalizedNavigationItems(bottomNavigationMenu?.children);
  return (
    <>
      <ul className="fixed bottom-0 left-0 z-9 gap-3 mb-0 pb-[10px] flex w-full items-end justify-between bg-white px-4 shadow-[0_0_10px_#0003]">
        {navigationItems.length > 0 &&
          navigationItems.map((item, index: number) => {
            const isQuickActions = !isAppLayout && item.nodeName === "item2";
            const isHamburger = item.nodeName === "item4";
            const navigationClasses = getNavigationItemClasses(isQuickActions, isHamburger);
            const iconClass = item.iconClass;
            const navigationImage = getNavigationImage(item, isQuickActions);

            return (
              <li key={item.nodeName || index} className="fs-10 h-[63px] flex-1 text-center">
                <BFLLink
                  href={!isQuickActions && !isHamburger ? item.ctaLink : undefined}
                  onClick={() => {
                    // Track analytics for bottom navigation icon click (all 6 icons)
                    handleBottomNavigationIconClickDL(
                      item.label,
                      index + 1,
                      item.ctaLink ?? "",
                    );
                    if (isHamburger) {
                      if (isOpenInApp()) {
                        openRedirectURL(HAMBURGER_PROMPT_URL);
                      } else {
                        setActivePanel((currentPanel) =>
                          currentPanel === "hamburger" ? "none" : "hamburger"
                        );
                      }
                    } else if (isQuickActions) {
                      setActivePanel((currentPanel) =>
                        currentPanel === "quickActions" ? "none" : "quickActions"
                      );
                    }
                  }}
                  className={`relative flex h-full w-full box-border flex-col items-center justify-start overflow-hidden pt-3 ${navigationClasses}`}
                >
                  {navigationImage ? (
                    <img
                      src={navigationImage}
                      alt={item.scanQrGifAltText || item.primaryImgAltText || item.label}
                      className="h-6 max-h-6 w-6 max-w-6 shrink-0 object-contain"
                    />
                  ) : (
                    iconClass && (
                      <i className={`${iconClass} inline-flex h-6 w-6 shrink-0 items-center justify-center leading-none`} />
                    )
                  )}
                  <span className="mt-[3px] text-[10px] leading-[11px]">{item.label}</span>
                </BFLLink>
              </li>
            );
          })}
      </ul>

      <Conditional if={activePanel === "quickActions"}>
        <button
          className="bg-opacity-50 fixed inset-0 z-7 border-none bg-[rgba(0,0,0,0.5)]"
          onClick={() => setActivePanel("none")}
          aria-label="Close quick actions"
        />
      </Conditional>

      <Conditional if={activePanel === "hamburger"}>
        <HamburgerMenu
          chipsDetails={chipsDetails}
          hamburgerMenuList={hamburgerMenuList}
          mobileFollowus={mobileFollowus}
          mainMenuList={mainMenuList}
          closeHamburgerMenu={() => {
            setActivePanel("none");
          }}
        />
      </Conditional>

      <Conditional if={activePanel === "quickActions"}>
        <QuickActions quickActions={quickActions} />
      </Conditional>
    </>
  );
};

export default BottomNavigation;
